import siteConfig from "../config/siteConfig";

export const STORAGE_KEYS = Object.freeze({
  cart: `${siteConfig.storageNamespace}:cart:v1`,
  wishlist: `${siteConfig.storageNamespace}:wishlist:v1`,
  user: `${siteConfig.storageNamespace}:user:v1`,
  freeSample: `${siteConfig.storageNamespace}:free-sample:v1`,
});

export const LEGACY_CART_STORAGE_KEY = "cartItems";
export const DEFAULT_FREE_SHIPPING_THRESHOLD = siteConfig.shipping.freeThreshold;
export const DEFAULT_SHIPPING_RATE = siteConfig.shipping.standardRate;
export const DEFAULT_VARIANT_ID = "default";

const isObject = (value) => value !== null && typeof value === "object";

export const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
};

export const toNumber = (value, fallback = 0) => {
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const roundCurrency = (value) =>
  Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;

export const normalizeQuantity = (value, maximum = Number.POSITIVE_INFINITY) => {
  const parsed = Math.floor(toNumber(value));

  if (parsed <= 0) {
    return 0;
  }

  const safeMaximum = Number.isFinite(maximum)
    ? Math.max(0, Math.floor(maximum))
    : Number.POSITIVE_INFINITY;

  return Math.min(parsed, safeMaximum);
};

export const readStorage = (key, fallbackValue) => {
  if (typeof window === "undefined") {
    return fallbackValue;
  }

  try {
    const serializedValue = window.localStorage.getItem(key);
    return serializedValue === null
      ? fallbackValue
      : JSON.parse(serializedValue);
  } catch {
    return fallbackValue;
  }
};

export const writeStorage = (key, value) => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(value));
    }

    return true;
  } catch {
    return false;
  }
};

export const getProductId = (productOrId) =>
  normalizeId(
    isObject(productOrId)
      ? productOrId.productId ?? productOrId.id ?? productOrId.slug
      : productOrId
  );

export const getVariantId = (variantOrId) =>
  normalizeId(
    isObject(variantOrId)
      ? variantOrId.variantId ?? variantOrId.id ?? variantOrId.sku
      : variantOrId
  );

export const createCartLineId = (productOrId, variantOrId) => {
  const productId = getProductId(productOrId);
  const variantId = getVariantId(variantOrId) || DEFAULT_VARIANT_ID;

  return `${encodeURIComponent(productId)}::${encodeURIComponent(variantId)}`;
};

export const findCatalogProduct = (catalog, productOrId) => {
  const productId = getProductId(productOrId);
  const products = Array.isArray(catalog) ? catalog : [];
  const matchedProduct = products.find(
    (product) =>
      getProductId(product) === productId ||
      normalizeId(product.slug) === productId
  );

  if (matchedProduct) {
    return matchedProduct;
  }

  return isObject(productOrId) ? productOrId : null;
};

export const getProductVariants = (product) =>
  Array.isArray(product?.variants) ? product.variants : [];

export const findProductVariant = (product, variantOrId) => {
  const variants = getProductVariants(product);

  if (variants.length === 0) {
    return null;
  }

  if (
    variantOrId === null ||
    variantOrId === undefined ||
    variantOrId === ""
  ) {
    return variants[0];
  }

  const reference = isObject(variantOrId) ? variantOrId : {};
  const variantId = getVariantId(variantOrId);
  const variantLabel = normalizeId(
    reference.label ?? reference.name ?? variantOrId
  );
  const variantWeight = normalizeId(
    reference.weight ?? reference.size ?? variantOrId
  );

  return (
    variants.find(
      (variant) =>
        getVariantId(variant) === variantId ||
        normalizeId(variant.sku) === variantId ||
        normalizeId(variant.label ?? variant.name) === variantLabel ||
        normalizeId(variant.weight ?? variant.size) === variantWeight
    ) || null
  );
};

export const getUnitPrice = (product, variant) =>
  roundCurrency(variant?.price ?? product?.price ?? 0);

export const getAvailableStock = (product, variant) => {
  const value = variant?.stock ?? product?.stock;

  if (value === null || value === undefined || value === "") {
    return Number.POSITIVE_INFINITY;
  }

  return Math.max(0, Math.floor(toNumber(value)));
};

const resolveStoredVariantReference = (entry) =>
  entry.variantId ??
  entry.variant?.id ??
  entry.sku ??
  entry.weight ??
  entry.size ??
  null;

export const normalizeCartEntries = (entries, catalog = []) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  const products = Array.isArray(catalog) ? catalog : [];
  const mergedEntries = new Map();

  entries.forEach((entry) => {
    if (!isObject(entry)) {
      return;
    }

    const productId = getProductId(entry);

    if (!productId) {
      return;
    }

    const product = findCatalogProduct(products, productId);

    if (!product && products.length > 0) {
      return;
    }

    const variants = getProductVariants(product);
    const requestedVariant = resolveStoredVariantReference(entry);
    const variant = product
      ? findProductVariant(product, requestedVariant)
      : null;

    if (product && variants.length > 0 && !variant) {
      return;
    }

    const variantId = getVariantId(variant) ||
      getVariantId(requestedVariant) ||
      DEFAULT_VARIANT_ID;
    const stock = product
      ? getAvailableStock(product, variant)
      : Number.POSITIVE_INFINITY;
    const quantity = normalizeQuantity(entry.quantity, stock);

    if (quantity === 0) {
      return;
    }

    const lineId = createCartLineId(productId, variantId);
    const existingEntry = mergedEntries.get(lineId);
    const mergedQuantity = normalizeQuantity(
      (existingEntry?.quantity || 0) + quantity,
      stock
    );

    mergedEntries.set(lineId, {
      lineId,
      productId,
      variantId,
      quantity: mergedQuantity,
    });
  });

  return Array.from(mergedEntries.values());
};

const resolveImageSource = (image) => {
  if (typeof image === "string") {
    return image;
  }

  return isObject(image) ? image.src ?? image.url ?? "" : "";
};

export const getPrimaryProductImage = (product) => {
  const images = Array.isArray(product?.images) ? product.images : [];
  return resolveImageSource(images[0]) || resolveImageSource(product?.image);
};

export const hydrateCartEntries = (entries, catalog = []) =>
  normalizeCartEntries(entries, catalog)
    .map((entry) => {
      const product = findCatalogProduct(catalog, entry.productId);

      if (!product) {
        return null;
      }

      const variant = findProductVariant(product, entry.variantId);
      const unitPrice = getUnitPrice(product, variant);

      return {
        ...entry,
        product,
        variant,
        name: product.name,
        slug: product.slug,
        sku: variant?.sku ?? product.sku ?? "",
        variantLabel: variant?.label ?? variant?.name ?? "",
        variantWeight: variant?.weight ?? variant?.size ?? "",
        image: getPrimaryProductImage(product),
        unitPrice,
        price: unitPrice,
        lineTotal: roundCurrency(unitPrice * entry.quantity),
      };
    })
    .filter(Boolean);

export const calculateCartTotals = (
  cartItems,
  freeShippingThreshold = DEFAULT_FREE_SHIPPING_THRESHOLD,
  shippingRate = DEFAULT_SHIPPING_RATE
) => {
  const items = Array.isArray(cartItems) ? cartItems : [];
  const cartCount = items.reduce(
    (totalQuantity, item) =>
      totalQuantity + normalizeQuantity(item.quantity),
    0
  );
  const subtotal = roundCurrency(
    items.reduce(
      (runningTotal, item) =>
        runningTotal +
        toNumber(
          item.lineTotal,
          toNumber(
            item.unitPrice ?? item.price,
            getUnitPrice(item.product, item.variant)
          ) *
            normalizeQuantity(item.quantity)
        ),
      0
    )
  );
  const hasFlatRateShipping = toNumber(freeShippingThreshold) >= 0;
  const standardShipping = Math.max(0, toNumber(shippingRate));
  const freeShippingRemaining = 0;
  const shipping = roundCurrency(
    subtotal === 0 || !hasFlatRateShipping ? 0 : standardShipping
  );

  return {
    cartCount,
    subtotal,
    shipping,
    freeShippingRemaining,
    total: roundCurrency(subtotal + shipping),
    qualifiesForFreeShipping: false,
  };
};

export const normalizeWishlist = (items, catalog = []) => {
  if (!Array.isArray(items)) {
    return [];
  }

  const products = Array.isArray(catalog) ? catalog : [];
  const productIds = new Set();

  items.forEach((item) => {
    const productId = getProductId(item);

    if (!productId) {
      return;
    }

    if (products.length > 0 && !findCatalogProduct(products, productId)) {
      return;
    }

    productIds.add(productId);
  });

  return Array.from(productIds);
};

export const normalizeUserProfile = (profile, existingProfile = null) => {
  if (!isObject(profile)) {
    return existingProfile;
  }

  const existing = isObject(existingProfile) ? existingProfile : {};
  const email = String(profile.email ?? existing.email ?? "").trim();
  const firstName = String(
    profile.firstName ?? existing.firstName ?? ""
  ).trim();
  const lastName = String(
    profile.lastName ?? existing.lastName ?? ""
  ).trim();
  const fallbackName = [firstName, lastName].filter(Boolean).join(" ");
  const name = String(profile.name ?? existing.name ?? fallbackName).trim();
  const id = normalizeId(
    profile.id ?? existing.id ?? email.toLowerCase()
  );

  return {
    id: id || "local-user",
    email,
    name,
    firstName,
    lastName,
    avatar: String(profile.avatar ?? existing.avatar ?? "").trim(),
    addresses: Array.isArray(profile.addresses)
      ? profile.addresses
      : existing.addresses ?? [],
  };
};
